// contactService.js
// Handles all contact form functionality:
//   - Submitting data to Google Sheets
//   - Triggering email notification to info@kivastech.com

const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbwV9AsUnqqluUs35M5zDlGal7YEoJTIuZpedIh87yHGEix7aO6F8UlYQCAH2TPCiPQn/exec";

/**
 * Submits contact form data to Google Sheets via Apps Script Web App.
 * The Apps Script should be set up to:
 *   1. Append a row to the sheet with the submitted data
 *   2. Send an email notification to info@kivastech.com
 *
 * @param {Object} formData
 * @param {string} formData.name       - Full name (required)
 * @param {string} formData.email      - Email address (required)
 * @param {string} [formData.phone]    - Phone number (optional)
 * @param {string} [formData.message]  - Project details (optional)
 * @returns {Promise<{ success: boolean; error?: string }>}
 */
export async function submitContactForm(formData) {
  const { name, email, phone = "", message = "" } = formData;

  // Basic client-side validation
  if (!name || !name.trim()) {
    return { success: false, error: "Name is required." };
  }
  if (!email || !email.trim()) {
    return { success: false, error: "Email is required." };
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { success: false, error: "Please enter a valid email address." };
  }

  // Build payload
  const payload = {
    name: name.trim(),
    email: email.trim(),
    phone: phone.trim(),
    message: message.trim(),
    submittedAt: new Date().toISOString(),
    notifyEmail: "info@kivastech.com",
  };

  try {
    // Google Apps Script Web Apps require no-cors when called from browser
    // We use a URLSearchParams body so the script can read via e.parameter
    const body = new URLSearchParams(payload);

    const response = await fetch(GOOGLE_SCRIPT_URL, {
      method: "POST",
      mode: "no-cors", // required for Google Apps Script
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    });

    // With no-cors, response.type === "opaque" and we cannot read the body.
    // A successful fetch (no network error) is treated as a success.
    return { success: true };
  } catch (error) {
    console.error("[contactService] Submission failed:", error);
    return {
      success: false,
      error: "Network error. Please check your connection and try again.",
    };
  }
}

/**
 * Returns a formatted timestamp string for display / logging.
 * @returns {string}
 */
export function getFormattedTimestamp() {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date());
}
